
import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

interface Review {
  id: string;
  title: string;
  content: string;
  author: string;
  category: string;
  rating: number;
  cover_image: string;
  genre: string;
  verdict: string;
  bestseller: boolean;
  our_pick: boolean;
  created_at: string;
}

export default function ReviewDetail() {
  const { id } = useParams<{ id: string }>();
  const [review, setReview] = useState<Review | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchReview = async () => {
      if (!id) return;
      
      try {
        const { data, error } = await supabase
          .from('posts')
          .select('*')
          .eq('id', id)
          .eq('published', true)
          .single();

        if (error) throw error;
        setReview(data);
      } catch (error) {
        console.error('Error fetching review:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchReview();
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <main className="pt-20">
          <div className="max-w-4xl mx-auto px-4 py-12">
            <div className="animate-pulse">
              <div className="h-8 bg-gray-200 rounded w-3/4 mb-4"></div>
              <div className="h-4 bg-gray-200 rounded w-1/2 mb-8"></div>
              <div className="h-64 bg-gray-200 rounded mb-8"></div>
              <div className="space-y-4">
                <div className="h-4 bg-gray-200 rounded"></div>
                <div className="h-4 bg-gray-200 rounded"></div>
                <div className="h-4 bg-gray-200 rounded w-3/4"></div>
              </div>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (!review) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <main className="pt-20">
          <div className="max-w-4xl mx-auto px-4 py-12 text-center">
            <h1 className="text-2xl font-bold text-gray-900 mb-4">Review Not Found</h1>
            <p className="text-gray-600 mb-8">The review you're looking for doesn't exist or has been removed.</p>
            <Link 
              to="/reviews" 
              className="bg-red-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-red-700 transition-colors whitespace-nowrap"
            >
              Back to Reviews
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const renderStars = (rating: number) => {
    const numRating = rating;
    const fullStars = Math.floor(numRating);
    const hasHalfStar = numRating % 1 !== 0;
    
    return (
      <div className="flex items-center gap-1">
        {[...Array(fullStars)].map((_, i) => (
          <i key={i} className="ri-star-fill text-yellow-400 text-lg"></i>
        ))}
        {hasHalfStar && <i className="ri-star-half-fill text-yellow-400 text-lg"></i>}
        {[...Array(5 - Math.ceil(numRating))].map((_, i) => (
          <i key={i} className="ri-star-line text-gray-300 text-lg"></i>
        ))}
        <span className="ml-2 text-sm font-medium text-gray-700">{rating}/5</span>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main className="pt-20">
        {/* Breadcrumb */}
        <div className="bg-gray-50 py-4">
          <div className="max-w-4xl mx-auto px-4">
            <nav className="flex items-center space-x-2 text-sm text-gray-600">
              <Link to="/" className="hover:text-red-600 transition-colors">Home</Link>
              <i className="ri-arrow-right-s-line"></i>
              <Link to="/reviews" className="hover:text-red-600 transition-colors">Reviews</Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-gray-900">{review.title}</span>
            </nav>
          </div>
        </div>

        <article className="max-w-4xl mx-auto px-4 py-12">
          {/* Header */}
          <header className="mb-8">
            <div className="flex items-start gap-8">
              <div className="flex-shrink-0">
                <img 
                  src={review.cover_image} 
                  alt={review.title}
                  className="w-48 h-72 object-cover rounded-lg shadow-lg"
                />
              </div>
              <div className="flex-1">
                <h1 className="text-4xl font-bold text-gray-900 mb-4">{review.title}</h1>
                <div className="space-y-3 mb-6">
                  <p className="text-xl text-gray-700">by <span className="font-semibold">{review.author}</span></p>
                  <div className="flex items-center gap-4">
                    {renderStars(review.rating)}
                  </div>
                  <div className="flex items-center gap-4 text-sm text-gray-600">
                    <span className="bg-red-100 text-red-800 px-3 py-1 rounded-full font-medium">{review.genre}</span>
                    <span className="bg-gray-100 text-gray-800 px-3 py-1 rounded-full font-medium">{review.category}</span>
                    {review.our_pick && (
                      <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full font-medium flex items-center gap-1">
                        <span>🍁</span>
                        Our Pick
                      </span>
                    )}
                    {review.bestseller && (
                      <span className="bg-yellow-100 text-yellow-800 px-3 py-1 rounded-full font-medium flex items-center gap-1">
                        <i className="ri-award-line"></i>
                        Bestseller
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-500">Published on {formatDate(review.created_at)}</p>
                </div>
              </div>
            </div>
          </header>

          {/* Verdict Box */}
          {review.verdict && (
            <div className="bg-red-50 border-l-4 border-red-600 p-6 mb-8">
              <h3 className="text-lg font-semibold text-red-900 mb-2 flex items-center gap-2">
                <i className="ri-quote-text"></i>
                Our Verdict
              </h3>
              <p className="text-red-800 italic">{review.verdict}</p>
            </div>
          )}

          {/* Review Content */}
          <div className="prose prose-lg max-w-none">
            {review.content.split('\n\n').map((paragraph, index) => (
              <p key={index} className="mb-6 text-gray-700 leading-relaxed">
                {paragraph}
              </p>
            ))}
          </div>

          {/* Footer Actions */}
          <div className="mt-12 pt-8 border-t border-gray-200">
            <div className="flex items-center justify-between">
              <Link 
                to="/reviews" 
                className="flex items-center gap-2 text-red-600 hover:text-red-700 transition-colors font-medium"
              >
                <i className="ri-arrow-left-line"></i>
                Back to All Reviews
              </Link>
              <div className="flex items-center gap-4">
                <span className="text-sm text-gray-500">Share this review:</span>
                <div className="flex items-center gap-2">
                  <button className="w-8 h-8 flex items-center justify-center bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors">
                    <i className="ri-facebook-fill text-sm"></i>
                  </button>
                  <button className="w-8 h-8 flex items-center justify-center bg-blue-400 text-white rounded-full hover:bg-blue-500 transition-colors">
                    <i className="ri-twitter-fill text-sm"></i>
                  </button>
                  <button className="w-8 h-8 flex items-center justify-center bg-gray-600 text-white rounded-full hover:bg-gray-7  transition-colors">
                    <i className="ri-mail-line text-sm"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </div>
  );
}
